import { BoxProgramSlotDefaultSettingsEntity } from "@/modules/boxProgrammation/entities/boxProgramSlotDefaultSettingsEntity"
import { boxProgrammationKeys } from "@/modules/boxProgrammation/gateway/boxProgramKeys"
import type { BoxProgramSlotDefaultSettingsInputDto } from "@/modules/boxProgrammation/gateway/boxProgramSlotDefaultSettingsDto"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useBoxProgramSlotDefaultSettings = (boxProgramId: string) => {
  const { boxProgramSlotApi } = useServicesContext()
  return useSuspenseQuery({
    queryKey: boxProgrammationKeys.defaultSettings(boxProgramId),
    queryFn: async () => {
      const resultDto = await boxProgramSlotApi.getBoxProgramSlotDefaultValues(boxProgramId)
      return BoxProgramSlotDefaultSettingsEntity.fromDto(resultDto)
    },
  })
}

export const useFetchBoxProgramSlotDefaultSettings = (boxProgramId: string | undefined) => {
  const { boxProgramSlotApi } = useServicesContext()
  return useQuery({
    queryKey: boxProgrammationKeys.defaultSettings(boxProgramId),
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const resultDto = await boxProgramSlotApi.getBoxProgramSlotDefaultValues(boxProgramId!)
      return BoxProgramSlotDefaultSettingsEntity.fromDto(resultDto)
    },
    enabled: !!boxProgramId,
  })
}

export const useCreateBoxProgramSlotDefaultSettings = () => {
  const { boxProgramSlotApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      boxProgramId,
      defaultValues,
    }: {
      boxProgramId: string
      defaultValues: BoxProgramSlotDefaultSettingsInputDto
    }) => {
      const resultDto = await boxProgramSlotApi.createBoxProgramSlotDefaultValues(boxProgramId, defaultValues)
      return BoxProgramSlotDefaultSettingsEntity.fromDto(resultDto)
    },
    onSuccess: (
      data: BoxProgramSlotDefaultSettingsEntity,
      variables: { boxProgramId: string; defaultValues: BoxProgramSlotDefaultSettingsInputDto },
    ) => {
      void queryClient.setQueryData(boxProgrammationKeys.defaultSettings(variables.boxProgramId), data)
    },
  })
}

export const useUpdateBoxProgramSlotDefaultSettings = () => {
  const { boxProgramSlotApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      boxProgramId,
      defaultValues,
    }: {
      boxProgramId: string
      defaultValues: BoxProgramSlotDefaultSettingsInputDto
    }) => {
      const resultDto = await boxProgramSlotApi.updateBoxProgramSlotDefaultValues(boxProgramId, defaultValues)
      return BoxProgramSlotDefaultSettingsEntity.fromDto(resultDto)
    },
    onSuccess: (
      data: BoxProgramSlotDefaultSettingsEntity,
      variables: { boxProgramId: string; defaultValues: BoxProgramSlotDefaultSettingsInputDto },
    ) => {
      void queryClient.setQueryData(boxProgrammationKeys.defaultSettings(variables.boxProgramId), data)
    },
  })
}
